import React, { useState, useEffect, useCallback } from "react";
import { DashboardLayout } from "../components/DashboardLayout";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import {
  Users,
  BookOpen,
  GraduationCap,
  UserPlus,
  Upload,
  Trash2,
  Link as LinkIcon,
  Loader2,
  CheckCircle2,
  AlertCircle,
  FileSpreadsheet,
  UserCheck,
  Plus,
  ChevronRight
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showLinkParent, setShowLinkParent] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);


  // New user form
  const [newUser, setNewUser] = useState({
    username: "",
    email: "",
    password: "123456789",
    role: "Student",
    name: "",
    idno: "",
    class_id: "",
    section_id: "",
    // Admission fields
    date_of_birth: "",
    gender: "",
    blood_group: "",
    address: "",
    previous_school: "",
    date_of_admission: new Date().toISOString().split("T")[0],
    phone: "",
    // Parent/guardian
    father: { name: "", phone: "", email: "" },
    mother: { name: "", phone: "", email: "" },
    guardian: { name: "", phone: "", email: "" },
    primary_contact: "Father",
  });

  useEffect(() => {
    if (showAddUser && newUser.role === "Student") {
      const studentCount = users.filter(u => u.role === "Student").length;
      const currentYear = new Date().getFullYear();
      const nextRoll = `SSIS${currentYear}${studentCount + 1}`;
      setNewUser(prev => ({
        ...prev,
        idno: prev.idno || nextRoll,
        password: prev.password === "123456789" ? nextRoll : prev.password
      }));
    }
  }, [showAddUser, newUser.role, users]);

  // Bulk upload settings
  const [bulkSettings, setBulkSettings] = useState({
    
    year: "1",
    section: "A"
  });

  // Parent link form
  const [parentLink, setParentLink] = useState({
    parent_id: "",
    student_username: "",
    student_usn_digits: ""
  });

  // Filter state
  const [filters, setFilters] = useState({
    role: "all",
    department: "all",
    year: "all",
    section: "all"
  });

  // Department list state
  const [classesList, setClassesList] = useState([]);
  const [sectionsList, setSectionsList] = useState([]);
  const [showAddDept, setShowAddDept] = useState(false);
  const [newDept, setNewDept] = useState({ name: "", code: "" });
  const [deptLoading, setDeptLoading] = useState(false);

  const years = ["1", "2", "3", "4"];

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, usersRes, deptsRes] = await Promise.all([
        apiClient.get("/dashboard/stats"),
        apiClient.get("/users"),
        apiClient.get(`/classes?t=${new Date().getTime()}`)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setClassesList(deptsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post("/users", newUser);
      toast.success("User created successfully");
      setShowAddUser(false);
      setNewUser({
        username: "",
        email: "",
        password: "123456789",
        role: "Student",
        name: "",
        idno: "",
        class_id: "",
        section_id: "",
        date_of_birth: "",
        gender: "",
        blood_group: "",
        address: "",
        previous_school: "",
        date_of_admission: new Date().toISOString().split("T")[0],
        phone: "",
        father: { name: "", phone: "", email: "" },
        mother: { name: "", phone: "", email: "" },
        guardian: { name: "", phone: "", email: "" },
        primary_contact: "Father",
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await apiClient.delete(`/users/${userId}`);
      toast.success("User deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const handleAddDept = async (e) => {
    e.preventDefault();
    setDeptLoading(true);
    try {
      await apiClient.post("/departments", newDept);
      toast.success("Department added successfully");
      setNewDept({ name: "", code: "" });
      setShowAddDept(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add department");
    } finally {
      setDeptLoading(false);
    }
  };

  const handleDeleteDept = async (id) => {
    if (!window.confirm("Are you sure? This may affect users linked to this department.")) return;
    try {
      await apiClient.delete(`/departments/${id}`);
      toast.success("Department removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete department");
    }
  };

  const handleLinkParent = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post("/users/link-parent", parentLink);
      toast.success("Parent linked to student successfully");
      setShowLinkParent(false);
      setParentLink({ parent_id: "", student_username: "", student_usn_digits: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to link parent");
    }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadProgress({ status: "uploading", message: "Uploading file..." });

    const formData = new FormData();
    formData.append("file", file);
    if (bulkSettings.department) formData.append("department", bulkSettings.department);
    formData.append("year", bulkSettings.year);
    formData.append("section", bulkSettings.section);

    try {
      const response = await apiClient.post("/users/bulk-upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setUploadProgress({
        status: "success",
        message: response.data.message,
        errors: response.data.errors
      });

      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      setUploadProgress({
        status: "error",
        message: error.response?.data?.detail || "Upload failed"
      });
      toast.error(error.response?.data?.detail || "Upload failed");
    }

    e.target.value = "";
  };

  const downloadTemplate = () => {
    const template = [
      { sno: 1, student_name: "John Doe", roll_no: "JUUG25BTECH00001", department: "Computer Science", year: "1", section: "A" },
      { sno: 2, student_name: "Jane Smith", roll_no: "JUUG25BTECH00002", department: "Electronics", year: "1", section: "B" }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Students");
    XLSX.writeFile(wb, "student_template.xlsx");
  };

  const getRoleBadge = (role) => {
    const variants = {
      Admin: "bg-purple-100 text-purple-700",
      Teacher: "bg-blue-100 text-blue-700",
      Student: "bg-emerald-100 text-emerald-700",
      Parent: "bg-amber-100 text-amber-700"
    };
    return <Badge className={variants[role]}>{role}</Badge>;
  };

  const parents = users.filter(u => u.role === "Parent");
  const students = users.filter(u => u.role === "Student");

  // Apply filters
  const filteredUsers = users.filter(user => {
    if (filters.role !== "all" && user.role !== filters.role) return false;

    if (filters.department !== "all") {
      const selectedDept = classesList.find(d =>
        String(d.code).toLowerCase().trim() === String(filters.department).toLowerCase().trim()
      );

      const normalize = (s) => String(s || "").toLowerCase().replace(/\s+/g, ' ').trim();

      const uDept = normalize(user.department);
      const fCode = normalize(filters.department);
      const fName = selectedDept ? normalize(selectedDept.name) : "";

      const isMatch = uDept === fCode || (fName && (uDept === fName || fName.includes(uDept) || uDept.includes(fName)));
      if (!isMatch) return false;
    }

    if (filters.year !== "all" && String(user.year || "") !== String(filters.year)) return false;
    if (filters.section !== "all" && String(user.section || "A") !== String(filters.section)) return false;

    return true;
  });

  if (loading) {
    return (
      <DashboardLayout title="Admin Dashboard">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#1a365d]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Admin Dashboard">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="card-hover metric-card cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/admin/users")}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Total Students</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats.total_students || 0}</p>
              </div>
              <div className="p-3 bg-emerald-100 rounded-full">
                <GraduationCap className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover metric-card cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/admin/users")}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Total Teachers</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats.total_teachers || 0}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover metric-card cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/admin/classes")}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Total Classes</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats.total_classes || 0}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <BookOpen className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover metric-card cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/admin/users")}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Parents Linked</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats.total_parents || 0}</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-full">
                <UserCheck className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="registration" className="space-y-6">
        <TabsList className="bg-white border">
          <TabsTrigger value="registration" data-testid="tab-registration">User Registration</TabsTrigger>
          
          <TabsTrigger value="bulk" data-testid="tab-bulk">Bulk Upload</TabsTrigger>
          <TabsTrigger value="link" data-testid="tab-link">Parent Linking</TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="registration">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>User Actions</CardTitle>
                <CardDescription>Create new accounts or link parents to students</CardDescription>
              </div>
              <div className="flex gap-2">
                <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#1a365d] hover:bg-[#102a43]" data-testid="add-user-btn">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add User
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Add New User</DialogTitle>
                      <DialogDescription>
                        Create a new user account. Password will be <strong>123456789</strong> by default.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleAddUser} className="space-y-4">
                      {/* Basic Info */}
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Full Name <span className="text-red-500">*</span></Label>
                          <Input
                            value={newUser.name}
                            onChange={(e) => {
                              const newName = e.target.value;
                              setNewUser({ 
                                ...newUser, 
                                name: newName,
                                username: newUser.role === "Student" ? newName.toLowerCase().replace(/\s+/g, '') : newUser.username
                              });
                            }}
                            placeholder="John Doe"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Role</Label>
                          <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Student">Student</SelectItem>
                              <SelectItem value="Teacher">Teacher</SelectItem>
                              <SelectItem value="Admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Username <span className="text-red-500">*</span></Label>
                          <Input
                            value={newUser.username}
                            onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                            placeholder="john.doe"
                            required
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Email <span className="text-red-500">*</span></Label>
                          <Input
                            type="email"
                            value={newUser.email}
                            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                            placeholder="john@ssis.edu.in"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Phone</Label>
                          <Input
                            value={newUser.phone}
                            onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                            placeholder="+91 98765 43210"
                          />
                        </div>
                      </div>

                      {newUser.role === "Student" && (
                        <>
                          {/* Student Details */}
                          <div className="border-t pt-4 mt-4">
                            <h3 className="text-sm font-semibold text-slate-700 mb-3">Student Details</h3>
                            <div className="grid grid-cols-3 gap-4">
                              <div className="space-y-2">
                                <Label>Roll Number <span className="text-red-500">*</span></Label>
                                <Input
                                  value={newUser.idno}
                                  onChange={(e) => setNewUser({ 
                                    ...newUser, 
                                    idno: e.target.value,
                                    password: newUser.role === "Student" ? e.target.value : newUser.password
                                  })}
                                  placeholder="R0001"
                                  required
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Class <span className="text-red-500">*</span></Label>
                                <Select value={newUser.class_id} onValueChange={async (v) => {
                                  setNewUser({ ...newUser, class_id: v, section_id: "" });
                                  const res = await apiClient.get(`/classes/${v}/sections`);
                                  setSectionsList(res.data);
                                }}>
                                  <SelectTrigger className={!newUser.class_id ? "border-red-300" : ""}>
                                    <SelectValue placeholder="Select Class" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {classesList.map(c => (
                                      <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <Label>Section <span className="text-red-500">*</span></Label>
                                <Select value={newUser.section_id} onValueChange={(v) => setNewUser({ ...newUser, section_id: v })} disabled={!newUser.class_id || sectionsList.length === 0}>
                                  <SelectTrigger className={!newUser.section_id ? "border-red-300" : ""}>
                                    <SelectValue placeholder="Select Section" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {sectionsList.map(s => (
                                      <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <div className="grid grid-cols-3 gap-4 mt-3">
                              <div className="space-y-2">
                                <Label>Date of Birth</Label>
                                <Input
                                  type="date"
                                  value={newUser.date_of_birth}
                                  onChange={(e) => setNewUser({ ...newUser, date_of_birth: e.target.value })}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Gender</Label>
                                <Select value={newUser.gender} onValueChange={(v) => setNewUser({ ...newUser, gender: v })}>
                                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Male">Male</SelectItem>
                                    <SelectItem value="Female">Female</SelectItem>
                                    <SelectItem value="Other">Other</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <Label>Blood Group</Label>
                                <Select value={newUser.blood_group} onValueChange={(v) => setNewUser({ ...newUser, blood_group: v })}>
                                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                                  <SelectContent>
                                    {["A+","A-","B+","B-","AB+","AB-","O+","O-"].map(bg => (
                                      <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4 mt-3">
                              <div className="space-y-2">
                                <Label>Date of Admission</Label>
                                <Input
                                  type="date"
                                  value={newUser.date_of_admission}
                                  onChange={(e) => setNewUser({ ...newUser, date_of_admission: e.target.value })}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Previous School</Label>
                                <Input
                                  value={newUser.previous_school}
                                  onChange={(e) => setNewUser({ ...newUser, previous_school: e.target.value })}
                                  placeholder="If transferred in"
                                />
                              </div>
                            </div>
                            <div className="mt-3 space-y-2">
                              <Label>Residential Address</Label>
                              <Input
                                value={newUser.address}
                                onChange={(e) => setNewUser({ ...newUser, address: e.target.value })}
                                placeholder="Full address"
                              />
                            </div>
                          </div>

                          {/* Parent/Guardian Details */}
                          <div className="border-t pt-4 mt-4">
                            <h3 className="text-sm font-semibold text-slate-700 mb-1">Parent / Guardian Details</h3>
                            <p className="text-xs text-slate-500 mb-3">At least one parent or guardian is required.</p>

                            {/* Father */}
                            <div className="bg-slate-50 rounded-lg p-3 mb-3">
                              <div className="flex items-center gap-2 mb-2">
                                <input type="radio" name="primary_contact" value="Father" checked={newUser.primary_contact === "Father"} onChange={(e) => setNewUser({ ...newUser, primary_contact: e.target.value })} />
                                <span className="text-sm font-medium text-slate-700">Father</span>
                                {newUser.primary_contact === "Father" && <Badge variant="outline" className="text-xs">Primary Contact</Badge>}
                              </div>
                              <div className="grid grid-cols-3 gap-3">
                                <Input placeholder="Father's Name" value={newUser.father.name} onChange={(e) => setNewUser({ ...newUser, father: { ...newUser.father, name: e.target.value } })} />
                                <Input placeholder="Phone (WhatsApp)" value={newUser.father.phone} onChange={(e) => setNewUser({ ...newUser, father: { ...newUser.father, phone: e.target.value } })} />
                                <Input placeholder="Email" type="email" value={newUser.father.email} onChange={(e) => setNewUser({ ...newUser, father: { ...newUser.father, email: e.target.value } })} />
                              </div>
                            </div>

                            {/* Mother */}
                            <div className="bg-slate-50 rounded-lg p-3 mb-3">
                              <div className="flex items-center gap-2 mb-2">
                                <input type="radio" name="primary_contact" value="Mother" checked={newUser.primary_contact === "Mother"} onChange={(e) => setNewUser({ ...newUser, primary_contact: e.target.value })} />
                                <span className="text-sm font-medium text-slate-700">Mother</span>
                                {newUser.primary_contact === "Mother" && <Badge variant="outline" className="text-xs">Primary Contact</Badge>}
                              </div>
                              <div className="grid grid-cols-3 gap-3">
                                <Input placeholder="Mother's Name" value={newUser.mother.name} onChange={(e) => setNewUser({ ...newUser, mother: { ...newUser.mother, name: e.target.value } })} />
                                <Input placeholder="Phone (WhatsApp)" value={newUser.mother.phone} onChange={(e) => setNewUser({ ...newUser, mother: { ...newUser.mother, phone: e.target.value } })} />
                                <Input placeholder="Email" type="email" value={newUser.mother.email} onChange={(e) => setNewUser({ ...newUser, mother: { ...newUser.mother, email: e.target.value } })} />
                              </div>
                            </div>

                            {/* Guardian */}
                            <div className="bg-slate-50 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <input type="radio" name="primary_contact" value="Guardian" checked={newUser.primary_contact === "Guardian"} onChange={(e) => setNewUser({ ...newUser, primary_contact: e.target.value })} />
                                <span className="text-sm font-medium text-slate-700">Guardian</span>
                                <span className="text-xs text-slate-400">(optional)</span>
                                {newUser.primary_contact === "Guardian" && <Badge variant="outline" className="text-xs">Primary Contact</Badge>}
                              </div>
                              <div className="grid grid-cols-3 gap-3">
                                <Input placeholder="Guardian's Name" value={newUser.guardian.name} onChange={(e) => setNewUser({ ...newUser, guardian: { ...newUser.guardian, name: e.target.value } })} />
                                <Input placeholder="Phone (WhatsApp)" value={newUser.guardian.phone} onChange={(e) => setNewUser({ ...newUser, guardian: { ...newUser.guardian, phone: e.target.value } })} />
                                <Input placeholder="Email" type="email" value={newUser.guardian.email} onChange={(e) => setNewUser({ ...newUser, guardian: { ...newUser.guardian, email: e.target.value } })} />
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                      <DialogFooter className="mt-6">
                        <Button type="submit" className="bg-[#1a365d] w-full">
                          {newUser.role === "Student" ? "Admit Student" : "Create User"}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="py-12 border-t">
              <div className="text-center space-y-4">
                <div className="p-4 bg-blue-50 rounded-full w-fit mx-auto text-blue-600">
                  <UserPlus className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">Registration Portal</h3>
                <p className="text-slate-500 max-w-sm mx-auto">
                  Use the <strong>Add User</strong> button above to register a single account, or the <strong>Bulk Upload</strong> tab for large batches.
                  Existing users can now be managed on the dedicated <strong>User Management</strong> page.
                </p>
                <Button variant="outline" onClick={() => navigate("/admin/users")} className="mt-4">
                  Go to User Management
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        

        {/* Bulk Upload Tab */}
        <TabsContent value="bulk">
          <Card>
            <CardHeader>
              <CardTitle>Bulk Student Registration</CardTitle>
              <CardDescription>Upload an Excel file to register multiple students at once</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="space-y-2">
                  <Label>Override Class <span className="text-red-500">*</span></Label>
                  <Select value={bulkSettings.department} onValueChange={(v) => setBulkSettings({ ...bulkSettings, department: v })}>
                    <SelectTrigger className={!bulkSettings.department ? "border-amber-300" : ""}>
                      <SelectValue placeholder="Select Class" />
                    </SelectTrigger>
                    <SelectContent>
                      {classesList.map(dept => (
                        <SelectItem key={dept.id} value={dept.name}>{dept.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-slate-500">Selected department will apply to all students in file</p>
                </div>

                <div className="space-y-2">
                  <Label>Override Year</Label>
                  <Select value={bulkSettings.year} onValueChange={(v) => setBulkSettings({ ...bulkSettings, year: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map(y => (
                        <SelectItem key={y} value={y}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Override Section</Label>
                  <div className="flex gap-2">
                    <Select value={bulkSettings.section} onValueChange={(v) => setBulkSettings({ ...bulkSettings, section: v })}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["A", "B", "C", "D"].map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="Custom"
                      className="w-20"
                      value={bulkSettings.section}
                      onChange={(e) => setBulkSettings({ ...bulkSettings, section: e.target.value.toUpperCase() })}
                    />
                  </div>
                  <p className="text-[10px] text-slate-500">Pick from list or type custom</p>
                </div>
              </div>

              <div className={`dropzone ${!bulkSettings.department ? 'opacity-50 pointer-events-none' : ''}`} data-testid="excel-dropzone">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleExcelUpload}
                  className="hidden"
                  id="excel-upload"
                  disabled={!bulkSettings.department}
                  data-testid="excel-input"
                />
                <label htmlFor="excel-upload" className={bulkSettings.department ? "cursor-pointer" : "cursor-not-allowed"}>
                  <FileSpreadsheet className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                  <p className="text-lg font-medium text-slate-700">
                    {bulkSettings.department ? 'Drop Excel file here or click to upload' : 'Please select a department first'}
                  </p>
                  <p className="text-sm text-slate-500 mt-2">Supported formats: .xlsx, .xls</p>
                </label>
              </div>

              {uploadProgress && (
                <div className={`p-4 rounded-lg ${uploadProgress.status === "success" ? "bg-emerald-50" :
                  uploadProgress.status === "error" ? "bg-red-50" : "bg-blue-50"
                  }`}>
                  <div className="flex items-center gap-3">
                    {uploadProgress.status === "uploading" && <Loader2 className="w-5 h-5 animate-spin text-blue-600" />}
                    {uploadProgress.status === "success" && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                    {uploadProgress.status === "error" && <AlertCircle className="w-5 h-5 text-red-600" />}
                    <p className={`font-medium ${uploadProgress.status === "success" ? "text-emerald-700" :
                      uploadProgress.status === "error" ? "text-red-700" : "text-blue-700"
                      }`}>
                      {uploadProgress.message}
                    </p>
                  </div>
                  {uploadProgress.errors?.length > 0 && (
                    <ul className="mt-3 text-sm text-red-600 list-disc pl-5">
                      {uploadProgress.errors.slice(0, 5).map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div className="bg-slate-50 rounded-lg p-6">
                <h3 className="font-medium text-slate-900 mb-3">Excel File Requirements</h3>
                <ul className="text-sm text-slate-600 space-y-2">
                  <li>• Required columns: <code className="bg-slate-200 px-1 rounded">student_name</code>, <code className="bg-slate-200 px-1 rounded">usn</code>, <code className="bg-slate-200 px-1 rounded">department</code>, <code className="bg-slate-200 px-1 rounded">year</code>, <code className="bg-slate-200 px-1 rounded">section</code></li>
                  <li>• Optional column: <code className="bg-slate-200 px-1 rounded">sno</code> (serial number)</li>
                  <li>• Email will be auto-generated as: roll_no@ssis.edu.in</li>
                  <li>• Default password: 123456789</li>
                </ul>
                <Button
                  variant="outline"
                  onClick={downloadTemplate}
                  className="mt-4"
                  data-testid="download-template"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Download Template
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Parent Linking Tab */}
        <TabsContent value="link">
          <Card>
            <CardHeader>
              <CardTitle>Link Parent to Student</CardTitle>
              <CardDescription>Connect parent accounts to their children's student accounts</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLinkParent} className="max-w-md space-y-6">
                <div className="space-y-2">
                  <Label>Select Parent</Label>
                  <Select value={parentLink.parent_id} onValueChange={(v) => setParentLink({ ...parentLink, parent_id: v })}>
                    <SelectTrigger data-testid="select-parent">
                      <SelectValue placeholder="Select a parent account" />
                    </SelectTrigger>
                    <SelectContent>
                      {parents.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.full_name} ({p.username})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Student Name</Label>
                  <Input
                    value={parentLink.student_username}
                    onChange={(e) => setParentLink({ ...parentLink, student_username: e.target.value })}
                    placeholder="Enter student's name (e.g., Tejasw.s)"
                    data-testid="student-name-link"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Last 5 digits of Roll No.</Label>
                  <Input
                    value={parentLink.student_usn_digits}
                    onChange={(e) => setParentLink({ ...parentLink, student_usn_digits: e.target.value })}
                    placeholder="e.g., 22291"
                    maxLength={5}
                    data-testid="usn-digits-link"
                  />
                </div>

                <Button type="submit" className="bg-[#1a365d]" data-testid="link-parent-btn">
                  <LinkIcon className="w-4 h-4 mr-2" />
                  Link Parent to Student
                </Button>
              </form>

              <div className="mt-8 pt-6 border-t">
                <h3 className="font-medium text-slate-900 mb-4">Current Parent-Student Links</h3>
                <div className="space-y-3">
                  {parents.map((parent) => {
                    const linkedStudent = students.find(s => s.id === parent.linked_student_id);
                    return (
                      <div key={parent.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-slate-900">{parent.full_name}</p>
                          <p className="text-sm text-slate-500">{parent.username}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {linkedStudent ? (
                            <>
                              <LinkIcon className="w-4 h-4 text-emerald-600" />
                              <span className="text-emerald-700 font-medium">{linkedStudent.full_name}</span>
                            </>
                          ) : (
                            <span className="text-slate-400 italic">Not linked</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
};
